import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import getDay from 'date-fns/getDay';
import parseISO from 'date-fns/parseISO';
import isNil from 'lodash/isNil';

import { CapacityPlanningCustomContributionTargetAri } from '@atlassian/ari/capacity-planning/custom-contribution-target';
import { JiraIssueAri } from '@atlassian/ari/jira/issue';
import {
	CapacityPlanningActionType,
	type CapacityPlanningPayload,
} from '@atlassian/conversation-assistant-message-actions-api/capacity-planning';

import {
	type CapacityPlanningAction,
	type CapacityPlanningActionsByIndex,
	type CapacityPlanningIds,
	type HydratedUser,
	type HydratedWork,
	ContributorIdOfNoPeopleGroup,
	type CapacityPlanningViewType,
	VIEW_TYPE,
} from './types';

function hasValidPayloadStructure(
	payload: CapacityPlanningPayload,
	workById: Map<string, HydratedWork>,
): boolean {
	switch (payload.action) {
		case CapacityPlanningActionType.UPDATE_CONTRIBUTIONS:
			return (
				payload.inputs.contributions.length > 0 &&
				canRenderActionWithWork(payload.inputs.contributions[0].workId, workById)
			);
		case CapacityPlanningActionType.ADD_CONTRIBUTOR_WORK:
		case CapacityPlanningActionType.REMOVE_CONTRIBUTOR_WORK:
			return (
				payload.inputs.contributorWorkAssociations.length > 0 &&
				canRenderActionWithWork(payload.inputs.contributorWorkAssociations[0].workId, workById)
			);
		case CapacityPlanningActionType.ADD_CONTRIBUTORS:
		case CapacityPlanningActionType.REMOVE_CONTRIBUTORS:
			return payload.inputs.contributorDataIds.length > 0;
		case CapacityPlanningActionType.CREATE_NON_PROJECT_WORK:
			return payload.inputs.name.length > 0 && payload.inputs.contributorDataId.length > 0;
		case CapacityPlanningActionType.RENAME_NON_PROJECT_WORK:
			return payload.inputs.id.length > 0 && payload.inputs.name.length > 0;

		case CapacityPlanningActionType.ADD_WORKS:
		case CapacityPlanningActionType.REMOVE_WORKS:
			// for the add/remove works action, even the inputs.workDataIds expects an array of work ids,
			// but actually we only support one work id at a time, so we check the length of the array and the first element
			return (
				payload.inputs.workDataIds.length > 0 &&
				canRenderActionWithWork(payload.inputs.workDataIds[0], workById)
			);
		default:
			return false;
	}
}

function canRenderActionWithWork(workId: string, workById: Map<string, HydratedWork>): boolean {
	if (isNil(workId)) {
		return false;
	}
	const work = workById.get(workId);
	if (!work) {
		return false;
	}
	return !isNil(work.hierarchyLevel) || isCustomContributionTargetARI(workId);
}

export function isJiraWorkARI(workId: string): boolean {
	return JiraIssueAri.check(workId);
}

export function isCustomContributionTargetARI(workId: string): boolean {
	return CapacityPlanningCustomContributionTargetAri.check(workId);
}

export function getContributorDataId(payload: CapacityPlanningPayload): string | null {
	switch (payload.action) {
		case CapacityPlanningActionType.UPDATE_CONTRIBUTIONS: {
			const first = payload.inputs.contributions?.[0];
			return first?.contributorDataId ?? null;
		}
		case CapacityPlanningActionType.ADD_CONTRIBUTOR_WORK:
		case CapacityPlanningActionType.REMOVE_CONTRIBUTOR_WORK: {
			const first = payload.inputs.contributorWorkAssociations?.[0];
			return first?.contributorDataId ?? null;
		}
		case CapacityPlanningActionType.ADD_CONTRIBUTORS:
		case CapacityPlanningActionType.REMOVE_CONTRIBUTORS: {
			const first = payload.inputs.contributorDataIds?.[0];
			return first ?? null;
		}
		case CapacityPlanningActionType.CREATE_NON_PROJECT_WORK:
			return payload.inputs.contributorDataId ?? null;
		case CapacityPlanningActionType.RENAME_NON_PROJECT_WORK:
			return ContributorIdOfNoPeopleGroup;
		default:
			return null;
	}
}

export function getWorkId(payload: CapacityPlanningPayload): string | null {
	switch (payload.action) {
		case CapacityPlanningActionType.UPDATE_CONTRIBUTIONS: {
			const first = payload.inputs.contributions[0];
			return first?.workId ?? null;
		}
		case CapacityPlanningActionType.ADD_CONTRIBUTOR_WORK:
		case CapacityPlanningActionType.REMOVE_CONTRIBUTOR_WORK: {
			const first = payload.inputs.contributorWorkAssociations[0];
			return first?.workId ?? null;
		}
		case CapacityPlanningActionType.RENAME_NON_PROJECT_WORK:
			return payload.inputs.id ?? null;
		case CapacityPlanningActionType.ADD_WORKS:
		case CapacityPlanningActionType.REMOVE_WORKS:
			if (payload.inputs.workDataIds.length === 0) {
				return null;
			}
			// for the add/remove works action, even the inputs.workDataIds expects an array of work ids,
			// but actually we only support one work id at a time
			return payload.inputs.workDataIds[0] ?? null;
		default:
			return null;
	}
}

export function getIdsForHydration(actions: CapacityPlanningAction[]): CapacityPlanningIds {
	const contributorIdSet = new Set<string>();
	const workIdSet = new Set<string>();

	for (const action of actions) {
		const payload = action.data;
		if (isNil(payload)) {
			continue;
		}

		const workId = getWorkId(payload);
		if (!isNil(workId)) {
			workIdSet.add(workId);
		}

		const contributorId = getContributorDataId(payload);
		if (!isNil(contributorId)) {
			contributorIdSet.add(contributorId);
		}
	}

	return {
		contributorIds: Array.from(contributorIdSet),
		workIds: Array.from(workIdSet),
	};
}

export function filterActionsByIndexWithHydration(
	actions: CapacityPlanningAction[],
	usersByAccountId: Map<string, HydratedUser>,
	workById: Map<string, HydratedWork>,
	viewType: CapacityPlanningViewType,
): CapacityPlanningActionsByIndex {
	const actionsByWorkId = new Map<string, CapacityPlanningAction[]>();
	const actionsByContributorId = new Map<string, CapacityPlanningAction[]>();

	for (const action of actions) {
		const payload = action.data;
		// in the hasValidPayloadStructure has checked if the work is valid
		if (!hasValidPayloadStructure(payload, workById)) {
			continue;
		}
		const contributorId = getContributorDataId(payload);
		// in the work view, the add/remove works action will not have a contributor id, but it is valid to have a work id
		// so we check if the contributor id is nil or not in the people view
		if (
			viewType === VIEW_TYPE.PEOPLE &&
			(isNil(contributorId) ||
				(!usersByAccountId.has(contributorId) && contributorId !== ContributorIdOfNoPeopleGroup))
		) {
			continue;
		}
		if (!isNil(contributorId)) {
			const actionsList = actionsByContributorId.get(contributorId) ?? [];
			actionsList.push(action);
			actionsByContributorId.set(contributorId, actionsList);
		}

		const workId = getWorkId(payload);
		if (!isNil(workId)) {
			const workList = actionsByWorkId.get(workId) ?? [];
			workList.push(action);
			actionsByWorkId.set(workId, workList);
		}
	}

	return {
		actionsByWorkId,
		actionsByContributorId,
	};
}

/**
 * Resolves the effective status of an action by preferring the store value (getStatus) over the BE payload (action.status)
 */
export function getEffectiveStatus(
	action: CapacityPlanningAction,
	getStatus: (a: CapacityPlanningAction) => string,
): string {
	const storeStatus = getStatus(action);
	return storeStatus !== 'idle' ? storeStatus : (action.status ?? 'idle');
}

export function isActionCompleted(
	action: CapacityPlanningAction,
	getStatus: (a: CapacityPlanningAction) => string,
): boolean {
	const status = getEffectiveStatus(action, getStatus);
	return status === 'finished' || status === 'errored' || status === 'cancelled';
}

/**
 * Counts the number of weekdays (Monday–Friday) between two date strings (inclusive).
 * Returns 1 if dates are invalid or the range is empty, to avoid division by zero.
 */
export function countWeekdays(startDateStr: string, endDateStr: string): number {
	const start = parseISO(startDateStr);
	const end = parseISO(endDateStr);

	if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) {
		return 1;
	}

	const count = eachDayOfInterval({ start, end }).filter((d) => {
		const day = getDay(d);
		return day !== 0 && day !== 6;
	}).length;
	return count > 0 ? count : 1;
}

/**
 * Converts a total hours value over a date range into an equivalent weekly (per 5 weekdays) value.
 * For example, 40 total hours over 4 weekdays = 50 hours per week.
 */
export function getWeeklyHours(
	totalHours: number,
	startDateStr: string,
	endDateStr: string,
): number {
	const weekdays = countWeekdays(startDateStr, endDateStr);
	return parseFloat(((totalHours / weekdays) * 5).toFixed(2));
}
